/** One page of a server-paged list, with the total the filters match. */
export interface ListPage<T> {
  results: T[]
  totalCount: number
  page: number
  pageSize: number
}

/** The order a server-paged list is sorted in — an API field and a direction. */
export interface ListSort {
  orderBy: string
  orderByDirection: 'ASCENDING' | 'DESCENDING'
}

/**
 * Order parameters for a list request: the sort picked in the list, or the
 * list's own default order while nothing is picked. A picked sort keeps the
 * blanks at the bottom whichever way it runs.
 */
export function sortParams(
  sort: ListSort | null | undefined,
  defaults: Record<string, string>
): Record<string, string> {
  return sort ? { ...sort, nullsOrderPolicy: 'LAST' } : defaults
}

/** The most rows the API's list endpoints return in one page. */
export const MAX_PAGE_SIZE = 100

/** How many full pages a whole-list walk reads before it stops. */
const MAX_PAGES = 25

/**
 * Every row a paged list endpoint matches, for downloads and pickers — a list
 * on screen should ask for the page it shows. The first page usually reports
 * the total, in which case the pages after it are fetched at once; without a
 * total they are walked one at a time until a page comes back short.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<{ results: T[]; totalCount?: number }>
): Promise<T[]> {
  const first = await fetchPage(1, MAX_PAGE_SIZE)
  if (first.results.length < MAX_PAGE_SIZE) return first.results

  if (first.totalCount !== undefined) {
    const pageCount = Math.min(Math.ceil(first.totalCount / MAX_PAGE_SIZE), MAX_PAGES)
    const rest = await Promise.all(
      Array.from({ length: pageCount - 1 }, (_, index) => fetchPage(index + 2, MAX_PAGE_SIZE))
    )
    return [...first.results, ...rest.flatMap((page) => page.results)]
  }

  const all = [...first.results]
  for (let page = 2; page <= MAX_PAGES; page++) {
    const next = await fetchPage(page, MAX_PAGE_SIZE)
    all.push(...next.results)
    if (next.results.length < MAX_PAGE_SIZE) break
  }
  return all
}
